<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import draggable from 'vuedraggable';
  import CategoriesService from '@/api/services/categories.service';
  import ItemsService from '@/api/services/items.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import V2MenuCategorySelect from '@/components/v2/menu-import/editor/V2MenuCategorySelect.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useMenuStore } from '@/composition/use-menu';
  import { useToastObstacle } from '@/composition/use-toast-obstacle';
  import { useUi } from '@/composition/use-ui';
  import {
    indexesOf,
    moveStep,
    moveToEdge,
    orderBody,
    pendingKeys,
    productsListKey,
    reconcileList,
    TReorderListKey,
    TReorderPending,
    withPendingList,
  } from '@/domain/catalog/reorder';
  import { AlertType } from '@/interfaces/IAlert';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Ordenar el menú» (plan §5.11): the categories, or one category's
   * products, dragged by ⠿ or moved with the arrows. Nothing changes in the
   * menu until «Guardar orden», which saves each touched list through the
   * transactional `saveOrder` routes.
   */
  interface IReorderRow {
    id: number;
    name: string;
  }

  type TReorderTab = 'categories' | 'products';

  const { state, data, online, commands, actions } = useCatalogPage();
  const menuStore = useMenuStore();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const categoryLabelId = nextDomId('reorder-category');
  const footer = ref<HTMLElement | null>(null);
  useToastObstacle(footer);

  let disposed = false;
  onBeforeUnmount(() => {
    disposed = true;
  });

  const tab = ref<TReorderTab>(
    state.reorderCategoryId !== null ? 'products' : 'categories'
  );
  const tabs = computed(() => [
    { id: 'categories', label: t('menuPage.reorder.categories') },
    { id: 'products', label: t('menuPage.reorder.products') },
  ]);

  const sections = computed(() => data.sections.value);
  const productCategoryId = ref<number | null>(
    state.reorderCategoryId ?? sections.value[0]?.category.id ?? null
  );
  // The chosen category was deleted elsewhere: the first one takes over.
  watch(sections, (list) => {
    const id = productCategoryId.value;
    if (id === null || !list.some((section) => section.category.id === id))
      productCategoryId.value = list[0]?.category.id ?? null;
  });

  const categoryOptions = computed(() =>
    sections.value.map((section) => ({
      value: String(section.category.id),
      label: section.category.name,
    }))
  );

  const pending = ref<TReorderPending>({});
  watch(pending, (next) => {
    state.reorderDirty = pendingKeys(next).length > 0;
  });
  const dirty = computed(() => pendingKeys(pending.value).length > 0);
  const saving = ref(false);

  function savedIdsOf(key: TReorderListKey): number[] {
    if (key === 'categories')
      return sections.value.map((section) => section.category.id);
    const id = Number(key.slice(4));
    const section = sections.value.find((entry) => entry.category.id === id);
    return section ? section.items.map((item) => item.id) : [];
  }

  const listKey = computed<TReorderListKey | null>(() => {
    if (tab.value === 'categories') return 'categories';
    return productCategoryId.value !== null
      ? productsListKey(productCategoryId.value)
      : null;
  });

  const ids = computed<number[]>(() => {
    const key = listKey.value;
    if (!key) return [];
    const saved = savedIdsOf(key);
    const list = pending.value[key];
    return list ? reconcileList(list, saved) : saved;
  });

  const rows = computed<IReorderRow[]>(() =>
    ids.value.map((id) => ({
      id,
      name:
        tab.value === 'categories'
          ? data.categoryById.value.get(id)?.name ?? ''
          : data.itemById.value[id]?.name ?? '',
    }))
  );

  function setIds(next: number[]) {
    const key = listKey.value;
    if (!key) return;
    pending.value = withPendingList(pending.value, key, next, savedIdsOf(key));
  }

  function onDrag(next: IReorderRow[]) {
    setIds(next.map((row) => row.id));
  }

  function toEdge(id: number, edge: 'start' | 'end', close: () => void) {
    close();
    setIds(moveToEdge(ids.value, id, edge));
  }

  //#region Save

  async function saveList(
    restaurantId: number,
    key: TReorderListKey,
    list: number[]
  ) {
    const body = orderBody(list);
    if (key === 'categories') {
      await new CategoriesService().saveOrder(
        restaurantId,
        body.ids,
        body.indexes
      );
      menuStore.patchCategoryIndexes(indexesOf(list));
    } else {
      await new ItemsService().saveOrder(restaurantId, body.ids, body.indexes);
      menuStore.patchItemIndexes(indexesOf(list));
    }
  }

  /** Each list on its own: a failure keeps the rest pending. */
  async function save() {
    const restaurantId = data.restaurant.value?.id ?? null;
    if (saving.value || !dirty.value || restaurantId === null) return;
    saving.value = true;
    for (const key of pendingKeys(pending.value)) {
      const list = pending.value[key];
      if (!list) continue;
      const order = reconcileList(list, savedIdsOf(key));
      try {
        await saveList(restaurantId, key, order);
      } catch (error) {
        if (disposed) return;
        console.error('Saving the order failed', error);
        ui.showToast(AlertType.Error, t('menuPage.reorder.saveError'));
        saving.value = false;
        return;
      }
      if (disposed || data.restaurant.value?.id !== restaurantId) return;
      const next = { ...pending.value };
      delete next[key];
      pending.value = next;
    }
    saving.value = false;
    actions.track('reorder');
    ui.showToast(AlertType.Success, t('menuPage.reorder.saved'));
    state.reorderDirty = false;
    // Unmounts this panel: nothing may run after it.
    commands.exitReorder();
  }

  //#endregion
</script>

<template>
  <div class="v2-catalog-reorder-panel">
    <div class="panel-card">
      <PosUiSegmented
        class="panel-tabs"
        :value="tab"
        :items="tabs"
        block
        @input="tab = $event === 'products' ? 'products' : 'categories'"
      />

      <PosUiField
        v-if="tab === 'products'"
        class="panel-category"
        :label="t('menuPage.reorder.productsOf')"
        :label-id="categoryLabelId"
      >
        <V2MenuCategorySelect
          :value="productCategoryId !== null ? String(productCategoryId) : null"
          :options="categoryOptions"
          :label-id="categoryLabelId"
          :list-label="t('menuPage.reorder.productsOf')"
          @input="productCategoryId = Number($event)"
        />
      </PosUiField>

      <p class="panel-hint">{{ t('menuPage.reorder.hint') }}</p>

      <p v-if="!rows.length" class="panel-empty">
        {{ t('menuPage.reorder.empty') }}
      </p>
      <draggable
        v-else
        class="panel-list"
        tag="ol"
        handle=".drag-handle"
        :value="rows"
        :animation="150"
        :disabled="saving"
        @input="onDrag"
      >
        <li v-for="(row, index) in rows" :key="row.id" class="reorder-row">
          <span
            class="drag-handle"
            :title="t('menuPage.reorder.drag')"
            aria-hidden="true"
          >
            <i class="mdi mdi-drag-vertical" />
          </span>
          <span class="row-position">{{ index + 1 }}</span>
          <span class="row-name" :title="row.name">{{ row.name }}</span>
          <button
            type="button"
            class="row-arrow"
            :aria-label="t('menuPage.reorder.up', { name: row.name })"
            :disabled="index === 0 || saving"
            @click="setIds(moveStep(ids, row.id, -1))"
          >
            <i class="mdi mdi-arrow-up" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="row-arrow"
            :aria-label="t('menuPage.reorder.down', { name: row.name })"
            :disabled="index === rows.length - 1 || saving"
            @click="setIds(moveStep(ids, row.id, 1))"
          >
            <i class="mdi mdi-arrow-down" aria-hidden="true" />
          </button>
          <PosUiPopover class="row-more" placement="bottom" align="end">
            <template #trigger="{ toggle }">
              <button
                type="button"
                class="row-arrow"
                :aria-label="t('menuPage.reorder.more', { name: row.name })"
                :disabled="saving"
                @click="toggle"
              >
                <i class="mdi mdi-dots-horizontal" aria-hidden="true" />
              </button>
            </template>
            <template #default="{ close }">
              <div class="more-menu">
                <button
                  type="button"
                  class="more-entry"
                  :disabled="index === 0"
                  @click="toEdge(row.id, 'start', close)"
                >
                  {{ t('menuPage.reorder.toStart') }}
                </button>
                <button
                  type="button"
                  class="more-entry"
                  :disabled="index === rows.length - 1"
                  @click="toEdge(row.id, 'end', close)"
                >
                  {{ t('menuPage.reorder.toEnd') }}
                </button>
              </div>
            </template>
          </PosUiPopover>
        </li>
      </draggable>
    </div>

    <div ref="footer" class="panel-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="commands.closeReorder()"
        >{{ $t('cancel') }}</PosUiButton
      >
      <PosUiButton
        class="footer-save"
        :disabled="!dirty || saving || !online"
        @click="save"
        >{{ t('menuPage.reorder.save') }}</PosUiButton
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-reorder-panel {
    // Room for the fixed footer at the end of the list.
    @apply tw-pb-24;

    .panel-card {
      @apply tw-mx-auto tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-p-4;
      max-width: 760px;
    }

    .panel-category {
      @apply tw-mt-4;
      max-width: 360px;
    }

    .panel-hint {
      @apply tw-mb-0 tw-mt-3 tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }

    .panel-empty {
      @apply tw-mb-0 tw-mt-4 tw-text-sm tw-text-neutral-500;
    }

    .panel-list {
      @apply tw-m-0 tw-mt-3 tw-list-none tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-p-0;
    }

    .reorder-row {
      @apply tw-flex tw-min-h-[52px] tw-items-center tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-bg-surface tw-pr-1;

      &:first-child {
        @apply tw-border-t-0;
      }

      &.sortable-ghost {
        @apply tw-bg-orange-50;
      }

      .drag-handle {
        @apply tw-flex tw-h-[52px] tw-w-11 tw-flex-none tw-cursor-grab tw-items-center tw-justify-center tw-text-xl tw-text-neutral-400;
        touch-action: none;
      }

      .row-position {
        @apply tw-w-7 tw-flex-none tw-text-sm tw-tabular-nums tw-text-neutral-400;
      }

      .row-name {
        @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium tw-text-neutral-900;
      }

      .row-more {
        @apply tw-flex-none;
      }

      .row-arrow {
        @apply tw-flex tw-h-11 tw-w-11 tw-flex-none tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-lg tw-text-neutral-500;

        &:hover:not(:disabled) {
          @apply tw-bg-neutral-100 tw-text-neutral-900;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }

        &:disabled {
          @apply tw-cursor-not-allowed tw-opacity-40;
        }
      }
    }

    .more-menu {
      @apply tw-flex tw-flex-col tw-p-1;
      min-width: 200px;

      .more-entry {
        @apply tw-flex tw-min-h-[44px] tw-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-text-left tw-text-sm tw-text-neutral-900;

        &:hover:not(:disabled) {
          @apply tw-bg-neutral-100;
        }

        &:disabled {
          @apply tw-cursor-not-allowed tw-text-neutral-400;
        }
      }
    }

    .panel-footer {
      @apply tw-fixed tw-flex tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-p-2 tw-shadow-lg;
      // Left of the docked «Asistente» panel (0 px otherwise).
      right: calc(16px + var(--assistant-inset-right, 0px));
      bottom: calc(16px + env(safe-area-inset-bottom));
      z-index: 30;

      .footer-save {
        @apply tw-ml-2;
      }
    }
  }
</style>
